/* nat-probe.js — what a home network does to the UDP traffic of an online game, measured from a
   browser on that network. Owner: /nat-type-test. Background and the measurements behind every
   choice below — docs/why/nat-type-test.md.

   What it measures, and how (each verified in a real Chrome against KNOWN behaviour — a user-space
   NAT emulator with configurable mapping/filtering, and a UDP proxy that drops a known share):

   · MAPPING — does the router give one outside port to a socket whatever it talks to (endpoint-
     independent, the "cone" family) or a new one per destination (dependent, "symmetric")? One
     RTCPeerConnection asks several STUN servers on different IPs from ONE socket. Chrome drops a
     server-reflexive candidate identical to one it already has, so an independent mapping yields
     ONE srflx candidate however many servers answered; a dependent one yields one per server
     (getStats names the server in `url`). Whether each server answered at all comes from
     separate single-server probes run alongside.
   · FILTERING — does the router let in a packet from an address the device never sent to? A
     second peer relayed through our TURN sends ICE checks to the device's srflx address while the
     device is told NOTHING about it (no remote candidates). A check that passes the router reaches
     the device, which answers it (peer-reflexive, RFC 8445), and the data channel opens; a filtered
     one never arrives. From the other region's TURN the check comes from a NEW IP; from the same
     region's TURN it comes from the IP the device's STUN query went to, on another port (coturn
     relays on the STUN listener's address). Open / address-restricted / port-restricted fall out.
   · HAIRPIN — two peers on this device that know only each other's PUBLIC address: does the router
     loop the traffic back (two consoles in one home)?
   · LINE QUALITY — loss, jitter and latency of a game-like UDP stream, looped through our TURN.
     NOT a data channel: SCTP under it has congestion control, so after a loss the window shrinks
     and messages wait INSIDE the browser — with 5 % dropped per leg a data-channel loop reported
     36.7 % against 9.75 % true. An Opus RTP stream (50 packets/s, no congestion window) reported
     10.7 % against the same 9.75 %, 25.5 % against 27.75 %, 0 against 0: the browser counts
     missing sequence numbers itself (inbound-rtp.packetsLost) and computes RFC 3550 jitter. A
     10 Hz timestamped ping on a data channel beside it gives the latency timeline — the loop
     starts and ends on this page, so one clock times the whole trip.

   The page shows the answer in console terms; that mapping is community consensus, not an official
   Sony/Microsoft table (verdict() below), and the browser cannot see UPnP — the console may get a
   better type than the router gives a phone. Both branches are reported. */
(function (w) {
    'use strict';
    if (w.NatProbe && !w.NatProbe.__stub) return;

    var now = function () { return (w.performance && performance.now) ? performance.now() : Date.now(); };
    var sleep = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };

    /* ---------- servers ---------- */
    function regions() { return ['ru', 'intl']; }
    function other(region) { return region === 'ru' ? 'intl' : 'ru'; }
    function stunOf(region) {
        var R = w.__WebRTC;
        return R && R.stunUrl ? R.stunUrl(region) : null;
    }
    // TURN entries of a region, from the shared config (the only place that knows hosts and keys).
    function turnOf(region, transport) {
        var R = w.__WebRTC; if (!R || !R.iceServersFor) return null;
        var list = R.iceServersFor(true, region) || [];
        for (var i = 0; i < list.length; i++) {
            var u = String(list[i].urls || '');
            if (transport === 'udp' && /^turn:[^?]+$/.test(u)) return list[i];
            if (transport === 'tcp' && /^turn:.*transport=tcp/.test(u)) return list[i];
            if (transport === 'tls' && /^turns:/.test(u)) return list[i];
        }
        return null;
    }
    /* Third STUN server for the mapping comparison. Google's (stun.l.google.com) does not answer from
       Russia: UDP 19302 and 3478 from the RU box time out while our NL STUN (51 ms) and Cloudflare's
       (11 ms) answer; from NL Google works (measured 04.11.2026). Cloudflare answers on both sides. */
    var EXTRA_STUN = ['stun:stun.cloudflare.com:3478'];
    /* "Under load": a bounded download beside the loop — at most LOAD.capBytes, for LOAD.windowMs. The
       page prints the cap from here (CFG.loadMb), so the number on the label is the number enforced. */
    var LOAD = { capBytes: 300e6, windowMs: 10000 };
    function hostOf(url) { var m = /^stuns?:([^:?]+)/.exec(url || ''); return m ? m[1] : ''; }

    /* ---------- RTCPeerConnection plumbing ---------- */
    function hasRtc() { return typeof w.RTCPeerConnection === 'function'; }
    function gatherDone(pc, ms) {
        return new Promise(function (r) {
            if (pc.iceGatheringState === 'complete') return r();
            var t = setTimeout(r, ms);
            pc.addEventListener('icegatheringstatechange', function () {
                if (pc.iceGatheringState === 'complete') { clearTimeout(t); r(); }
            });
        });
    }
    function closeAll(list) { list.forEach(function (pc) { try { pc && pc.close(); } catch (_) {} }); }
    function onAbort(signal, fn) { if (signal) signal.addEventListener('abort', fn, { once: true }); }
    function aborted(signal) { return !!(signal && signal.aborted); }
    // getStats() of a peer that is closing can stay pending; nothing here may wait on it forever
    function statsOf(pc, ms) {
        if (!pc) return Promise.resolve(null);
        return Promise.race([pc.getStats().catch(function () { return null; }), sleep(ms || 1500).then(function () { return null; })]);
    }

    // One STUN server, one socket: did it answer, with which public address, how fast.
    function stunProbe(url, ms, signal) {
        return new Promise(function (resolve) {
            var res = { url: url, host: hostOf(url), ok: false, ip: '', port: 0, rttMs: -1, hosts: 0 };
            if (!hasRtc() || !url) return resolve(res);
            var pc, t0, done = false;
            function finish() { if (done) return; done = true; closeAll([pc]); resolve(res); }
            try {
                pc = probePc([{ urls: url }]);
                pc.createDataChannel('p');
                pc.onicecandidate = function (e) {
                    var c = e.candidate; if (!c || !c.candidate) return;
                    var type = candType(c);
                    if (type === 'host') res.hosts++;
                    if (type === 'srflx' && !res.ok && candProto(c) === 'udp' && candAddr(c).indexOf(':') < 0) {
                        res.ok = true; res.rttMs = Math.round(now() - t0);
                        res.ip = candAddr(c); res.port = candPort(c);
                        finish();
                    }
                };
                t0 = now();
                pc.createOffer().then(function (o) { return pc.setLocalDescription(o); })
                    .then(function () { return gatherDone(pc, ms); }).then(finish, finish);
                setTimeout(finish, ms + 50);
                onAbort(signal, finish);
            } catch (_) { finish(); }
        });
    }

    // Several STUN servers from ONE socket (see MAPPING above).
    function mappingProbe(urls, ms, signal) {
        return new Promise(function (resolve) {
            var res = { srflx: [], hostPorts: [], hostAddrs: [], ok: false };
            var seen = {}, addrSeen = {};
            if (!hasRtc()) return resolve(res);
            var pc, done = false;
            function finish() {
                if (done) return; done = true;
                statsOf(pc, 1500).then(function (st) {
                    // events give every srflx; stats add WHICH server produced it (Chrome; Firefox leaves url out)
                    if (st) st.forEach(function (s) {
                        if (s.type !== 'local-candidate') return;
                        if (s.candidateType === 'srflx' && (s.protocol || 'udp') === 'udp' && s.address && s.address.indexOf(':') < 0) {
                            var k = s.address + ':' + s.port;
                            if (seen[k]) { if (s.url && !seen[k].url) seen[k].url = s.url; }
                            else { seen[k] = { ip: s.address, port: s.port, url: s.url || '' }; res.srflx.push(seen[k]); }
                        }
                    });
                    res.ok = true; closeAll([pc]); resolve(res);
                });
            }
            try {
                pc = probePc(urls.map(function (u) { return { urls: u }; }));
                pc.createDataChannel('m');
                pc.onicecandidate = function (e) {
                    var c = e.candidate; if (!c || !c.candidate) return;
                    var type = candType(c), proto = candProto(c);
                    if (type === 'host' && proto === 'udp') {
                        res.hostPorts.push(candPort(c));
                        var ha = candAddr(c);
                        if (ha && !addrSeen[ha]) { addrSeen[ha] = 1; res.hostAddrs.push(ha); }
                    }
                    if (type === 'srflx' && proto === 'udp' && candAddr(c).indexOf(':') < 0) {
                        var k = candAddr(c) + ':' + candPort(c);
                        if (!seen[k]) { seen[k] = { ip: candAddr(c), port: candPort(c), url: '' }; res.srflx.push(seen[k]); }
                    }
                };
                pc.createOffer().then(function (o) { return pc.setLocalDescription(o); })
                    .then(function () { return gatherDone(pc, ms); }).then(finish, finish);
                setTimeout(finish, ms + 50);
                onAbort(signal, finish);
            } catch (_) { finish(); }
        });
    }

    /* Every probe peer is built the same way. Firefox (measured 30.10, Playwright build) does not
       start gathering for a peer that has ONLY a data channel, and the first peer of a page may not
       gather at all; an audio transceiver that only receives starts it, and max-bundle keeps all of
       it on ONE socket — the mapping comparison needs one socket. */
    function probePc(iceServers, extra) {
        var cfg = { iceServers: iceServers, bundlePolicy: 'max-bundle' };
        if (extra) Object.keys(extra).forEach(function (k) { cfg[k] = extra[k]; });
        var pc = new RTCPeerConnection(cfg);
        try { pc.addTransceiver('audio', { direction: 'recvonly' }); } catch (_) {}
        return pc;
    }
    function candType(c) { return c.type || (/ typ (\w+)/.exec(c.candidate || '') || [])[1]; }
    function candProto(c) { return (c.protocol || (c.candidate || '').split(' ')[2] || '').toLowerCase(); }
    function candAddr(c) { return c.address || (c.candidate || '').split(' ')[4] || ''; }
    function candPort(c) { return c.port || +((c.candidate || '').split(' ')[5] || 0); }

    /* A throwaway peer first: whatever a browser does on the first peer of a page (Firefox above),
       it happens to this one and not to a measurement. */
    function warmUp(url, signal) {
        return new Promise(function (resolve) {
            if (!hasRtc() || !url) return resolve();
            var pc, done = false;
            function finish() { if (done) return; done = true; closeAll([pc]); resolve(); }
            try {
                pc = probePc([{ urls: url }]); pc.createDataChannel('w');
                pc.onicecandidate = function (e) { if (e.candidate && candType(e.candidate) === 'srflx') finish(); };
                pc.createOffer().then(function (o) { return pc.setLocalDescription(o); }).catch(finish);
                setTimeout(finish, 1500);
                onAbort(signal, finish);
            } catch (_) { finish(); }
        });
    }

    var stripCandidates = function (sdp) {
        return sdp.split('\r\n').filter(function (l) { return l.indexOf('a=candidate') !== 0 && l.indexOf('a=end-of-candidates') !== 0; }).join('\r\n');
    };
    var keepCandidates = function (sdp, re) {
        return sdp.split('\r\n').filter(function (l) { return l.indexOf('a=candidate') !== 0 || re.test(l); }).join('\r\n');
    };

    /* Two peers on this page. A = the device under test (STUN only), B = the remote end.
       aSees=false: A is told nothing about B, so only a packet the router lets in can connect them. */
    function pairTest(o, signal) {
        return new Promise(function (resolve) {
            var A, B, ch, done = false, t0 = now();
            var res = { opened: false, ms: -1, pair: null };
            function finish() {
                if (done) return; done = true;
                closeAll([A, B]); resolve(res);
            }
            try {
                A = probePc(o.aServers);
                B = new RTCPeerConnection({ iceServers: o.bServers, iceTransportPolicy: o.bRelay ? 'relay' : 'all', bundlePolicy: 'max-bundle' });
                ch = A.createDataChannel('t', { ordered: false, maxRetransmits: 0 });
                ch.onopen = function () {
                    res.opened = true; res.ms = Math.round(now() - t0);
                    statsOf(A, 1500).then(function (st) {
                        if (!st) return;
                        var lc = {}, rc = {};
                        st.forEach(function (s) { if (s.type === 'local-candidate') lc[s.id] = s; if (s.type === 'remote-candidate') rc[s.id] = s; });
                        st.forEach(function (s) {
                            if (s.type === 'candidate-pair' && s.state === 'succeeded' && (s.nominated || s.selected)) {
                                res.pair = { l: (lc[s.localCandidateId] || {}).candidateType, r: (rc[s.remoteCandidateId] || {}).candidateType };
                            }
                        });
                    }).catch(function () {}).then(finish);
                };
                A.createOffer().then(function (of) { return A.setLocalDescription(of); })
                    .then(function () { return gatherDone(A, o.gatherMs || 4000); })
                    .then(function () {
                        var offer = A.localDescription.sdp;
                        if (o.aOnly) offer = keepCandidates(offer, o.aOnly);
                        if (!/ typ srflx/.test(offer) && o.needSrflx) { res.noSrflx = true; return finish(); }
                        return B.setRemoteDescription({ type: 'offer', sdp: offer })
                            .then(function () { return B.createAnswer(); })
                            .then(function (an) { return B.setLocalDescription(an); })
                            .then(function () { return gatherDone(B, o.gatherMs || 4000); })
                            .then(function () {
                                var ans = B.localDescription.sdp;
                                if (o.bOnly) ans = keepCandidates(ans, o.bOnly);
                                if (!o.aSees) ans = stripCandidates(ans);
                                if (o.needRelay && !/ typ relay/.test(B.localDescription.sdp)) { res.noRelay = true; return finish(); }
                                t0 = now();
                                return A.setRemoteDescription({ type: 'answer', sdp: ans });
                            });
                    })
                    .catch(function () { finish(); });
                setTimeout(finish, o.ms || 12000);
                onAbort(signal, finish);
            } catch (_) { finish(); }
        });
    }

    /* ---------- the NAT part ---------- */
    async function nat(opts) {
        opts = opts || {};
        var region = opts.region === 'ru' ? 'ru' : 'intl';
        var signal = opts.signal, step = opts.onStep || function () {};
        var out = { region: region, rtc: hasRtc() };
        if (!out.rtc) return out;

        // IPv6 beside everything else: Xbox uses native IPv6 where the network has it, around NAT
        var v6p = ipv6Probe(signal);

        // 1 — who answers, and the mapping
        step('udp', 'run');
        var ov = opts.override || {};      // stands only: emulated servers instead of ours
        var urls = ov.urls || [stunOf(region), stunOf(other(region))].concat(EXTRA_STUN).filter(Boolean);
        await warmUp(urls[0], signal);
        // Settle early: once two different servers have answered, the mapping comparison has what it
        // needs; a server that does not answer at all (Google from Russia, a filtered port) used to
        // hold every run for the full 4.5 s timeout. The grace after the second answer covers the
        // mapping peer's own candidates, which arrive about as fast as the single-server ones.
        var gather = new AbortController();
        onAbort(signal, function () { gather.abort(); });
        var nAnswered = 0, slowest = 0, settleTimer = null;
        var singles = urls.map(function (u) {
            return stunProbe(u, 4000, gather.signal).then(function (r) {
                if (r.ok) {
                    nAnswered++; slowest = Math.max(slowest, r.rttMs);
                    if (nAnswered >= 2 && !settleTimer) settleTimer = setTimeout(function () { gather.abort(); }, 600 + slowest);
                }
                return r;
            });
        });
        var both = await Promise.all([Promise.all(singles), mappingProbe(urls, 4500, gather.signal)]);
        clearTimeout(settleTimer);
        out.stun = both[0];
        out.mappingRaw = both[1];
        out.mapping = classifyMapping(out.stun, out.mappingRaw);
        out.publicIp = (out.stun.filter(function (s) { return s.ok; })[0] || {}).ip || '';
        if (aborted(signal)) return out;

        // No server-reflexive address from anyone = UDP did not get out. WHY decides the advice:
        //   · no local candidate at all  → the BROWSER keeps WebRTC off UDP (a "prevent WebRTC leak"
        //     extension, Brave shields, a privacy policy) — the console is not affected by that;
        //   · local candidates, TURN over TCP/TLS works → the NETWORK blocks UDP;
        //   · local candidates, no TURN at all, but our site answers → the network lets only web through;
        //   · nothing answers, not even the site → offline.
        // Before 04.11 a browser that hid its candidates read as "UDP passes" (QA M2).
        var anySrflx = out.stun.some(function (s) { return s.ok; }) || ((out.mappingRaw.srflx || []).length > 0);
        if (!anySrflx) {
            var hostCount = (out.mappingRaw.hostPorts || []).length + out.stun.reduce(function (a, s) { return a + (s.hosts || 0); }, 0);
            var tcp = await relayReachable(turnOf(region, 'tcp'), 6000, signal);
            var tls = tcp ? true : await relayReachable(turnOf(region, 'tls'), 7000, signal);
            var web = await webReachable(signal);
            out.tcpRelay = tcp || tls; out.web = web; out.hosts = hostCount;
            out.udp = !hostCount ? (out.tcpRelay || web ? 'browser' : 'offline')
                : out.tcpRelay ? 'blocked' : (web ? 'firewall' : 'offline');
            out.mapping = { kind: 'none', servers: 0, srflx: 0, bases: 0, confident: true, portPreserved: null, exits: 0 };
            out.ipv6 = await v6p;
            step('udp', 'done', out); step('map', 'skip'); step('filter', 'skip'); step('hairpin', 'skip');
            return out;
        }
        out.udp = 'ok';
        step('udp', 'done', out);
        step('map', 'done', out);

        // 2 — filtering + hairpin, in parallel (separate sockets, separate mappings)
        step('filter', 'run'); step('hairpin', 'run');
        var aStun = [{ urls: ov.aStun || stunOf(region) }];
        var near = ov.near !== undefined ? ov.near : turnOf(region, 'udp');
        var far = ov.far !== undefined ? ov.far : turnOf(other(region), 'udp');
        // The control pair (A told about B) shows how long a relayed connection takes on THIS line;
        // an unsolicited check that passes the router arrives as fast, so once the control has
        // opened at T the others get max(T, 3 s) more and then count as filtered — not a flat 14 s.
        var round = async function (capMs) {
            var link = function () { var c = new AbortController(); onAbort(signal, function () { c.abort(); }); return c; };
            var cF1 = link(), cF2 = link(), cH = link();
            var pControl = far ? pairTest({ aServers: aStun, bServers: [far], bRelay: true, aSees: true, aOnly: / typ srflx/, needSrflx: true, needRelay: true, ms: capMs }, signal) : Promise.resolve(null);
            var pF1 = far ? pairTest({ aServers: aStun, bServers: [far], bRelay: true, aSees: false, aOnly: / typ srflx/, needSrflx: true, needRelay: true, ms: capMs }, cF1.signal) : Promise.resolve(null);
            var pF2 = near ? pairTest({ aServers: aStun, bServers: [near], bRelay: true, aSees: false, aOnly: / typ srflx/, needSrflx: true, needRelay: true, ms: capMs }, cF2.signal) : Promise.resolve(null);
            var pH = pairTest({ aServers: aStun, bServers: aStun, bRelay: false, aSees: true, aOnly: / typ srflx/, bOnly: / typ srflx/, needSrflx: true, ms: Math.min(capMs, 10000) }, cH.signal);
            pControl.then(function (c) {
                if (!c || !c.opened) return;
                setTimeout(function () { cF1.abort(); cF2.abort(); }, Math.max(3000, c.ms));
            });
            return Promise.all([pControl, pF1, pF2, pH]);
        };
        var tests = await round(14000);
        // A control pair that did not open says nothing about the router (seen once in Firefox out
        // of several runs): one more round with a longer wait before calling filtering unknown.
        if (far && tests[0] && !tests[0].opened && !tests[0].noRelay && !aborted(signal)) {
            var again = await round(18000);
            tests[0] = again[0]; tests[1] = again[1]; tests[2] = again[2];
            if (tests[3] && !tests[3].opened && again[3]) tests[3] = again[3];
            out.filterRetried = true;
        }
        out.filterRaw = { control: tests[0], newIp: tests[1], sameIp: tests[2] };
        out.filtering = classifyFiltering(out.filterRaw);
        out.hairpin = classifyHairpin(tests[3], out.mapping);
        out.ipv6 = await v6p;
        step('filter', 'done', out); step('hairpin', 'done', out);
        return out;
    }

    /* Does this network reach the internet over IPv6? An IPv6-ONLY name answers only over IPv6.
       Own echo first (v6.tembrica.com: AAAA-only, DNS-only, NL — infra/nginx/nl-v6-tembrica.conf),
       the third-party one (v6.ident.me, also used by /ipv6-test) only if ours did not answer.
       true = IPv6 works · false = not found (no route and a block look the same from here) ·
       null = not measured (stopped). */
    var V6_ECHOES = ['https://v6.tembrica.com/ip', 'https://v6.ident.me/'];
    function ipv6Probe(signal) {
        if (typeof fetch !== 'function') return Promise.resolve(null);
        var one = function (url, ms) {
            var c = new AbortController();
            onAbort(signal, function () { c.abort(); });
            var t = setTimeout(function () { c.abort(); }, ms);
            return fetch(url, { cache: 'no-store', signal: c.signal, credentials: 'omit' })
                .then(function (r) { return r.ok ? r.text() : ''; })
                .then(function (txt) { return /:/.test(String(txt || '').trim()); })
                .catch(function () { return false; })
                .then(function (v) { clearTimeout(t); return v; });
        };
        return one(V6_ECHOES[0], 3000).then(function (v) {
            if (v || aborted(signal)) return aborted(signal) ? null : v;
            return one(V6_ECHOES[1], 2500).then(function (v2) { return aborted(signal) ? null : v2; });
        });
    }

    /* Does our own site answer at all (bounded)? Separates "the network lets only web pages through"
       from "offline" when no UDP and no TURN answered. */
    function webReachable(signal) {
        if (typeof fetch !== 'function') return Promise.resolve(false);
        var c = new AbortController();
        onAbort(signal, function () { c.abort(); });
        var t = setTimeout(function () { c.abort(); }, 4000);
        return fetch('/favicon.ico?nat=' + Math.random().toString(36).slice(2), { cache: 'no-store', signal: c.signal, credentials: 'omit' })
            .then(function (r) { return r.status > 0; })
            .catch(function () { return false; })
            .then(function (v) { clearTimeout(t); return v; });
    }

    function relayReachable(server, ms, signal) {
        return new Promise(function (resolve) {
            if (!server || !hasRtc()) return resolve(false);
            var pc, done = false;
            function finish(v) { if (done) return; done = true; closeAll([pc]); resolve(!!v); }
            try {
                pc = probePc([server], { iceTransportPolicy: 'relay' });
                pc.createDataChannel('r');
                pc.onicecandidate = function (e) { if (e.candidate && / typ relay/.test(e.candidate.candidate || '')) finish(true); };
                pc.createOffer().then(function (o) { return pc.setLocalDescription(o); }).catch(function () { finish(false); });
                setTimeout(function () { finish(false); }, ms);
                onAbort(signal, function () { finish(false); });
            } catch (_) { finish(false); }
        });
    }

    /* ---------- classification (pure) ---------- */
    function classifyMapping(stun, m) {
        var answered = (stun || []).filter(function (s) { return s.ok && s.ip && s.ip.indexOf(':') < 0; });
        // a server = its whole address (host AND port): an endpoint-independent mapping keeps one
        // outside port for every destination, so two ports of one host are two destinations too
        var hosts = {}; answered.forEach(function (s) { hosts[s.url] = 1; });
        var nServers = Object.keys(hosts).length;
        var srflx = (m && m.srflx) || [];
        var uniq = {}, ips = {}, urls = {};
        srflx.forEach(function (c) { uniq[c.ip + ':' + c.port] = c; ips[c.ip] = 1; if (c.url) urls[c.url] = (urls[c.url] || 0) + 1; });
        var nUniq = Object.keys(uniq).length, nIps = Object.keys(ips).length;
        var bases = Math.max(1, ((m && m.hostPorts) || []).length);
        var publicIps = {}; answered.forEach(function (s) { publicIps[s.ip] = 1; });
        var r = { kind: 'unknown', servers: nServers, srflx: nUniq, bases: bases, confident: bases === 1, portPreserved: null,
                  exits: Object.keys(publicIps).length };
        if (!answered.length && !nUniq) {
            // UDP closed = the device HAS local candidates and nobody outside answered them. No local
            // candidate at all is a probe that never started — not a verdict about the network.
            var anyHost = ((m && m.hostPorts) || []).length || (stun || []).some(function (s) { return s.hosts > 0; });
            r.kind = anyHost ? 'none' : 'unknown'; r.confident = !!anyHost; return r;
        }
        // Several public addresses: with several LOCAL interfaces (Wi-Fi + mobile data, Wi-Fi Assist,
        // a VPN adapter) each interface simply has its own way out — that says nothing about the
        // router; with one interface it is one network leaving through several exits (VPN split,
        // dual WAN, a load balancer). Interfaces are counted by host-candidate ADDRESS (one mDNS name
        // per interface): Firefox's RTP and RTCP sockets share it, so they are not two interfaces.
        var ifaces = Math.max(1, ((m && m.hostAddrs) || []).length);
        r.ifaces = ifaces;
        if (r.exits > 1 || nIps > 1) { r.kind = ifaces >= 2 ? 'interfaces' : 'multi'; return r; }
        if (nServers < 2 && nUniq <= 1) { r.kind = 'unknown'; return r; }         // nothing to compare against
        var urlHosts = Object.keys(urls).length;
        if (nUniq <= bases && urlHosts <= 1) r.kind = 'independent';
        else if ((urlHosts >= 2 || urlHosts === 0) && nServers >= 2 && nUniq >= 2 * bases) r.kind = 'dependent';   // Firefox: no url in stats, count only
        else if (bases === 1 && nUniq >= 2) r.kind = 'dependent';
        else r.kind = 'unknown';
        if (r.kind === 'independent' && srflx.length) {
            var hp = (m.hostPorts || []);
            r.portPreserved = hp.indexOf(srflx[0].port) >= 0;
        }
        return r;
    }

    function classifyFiltering(f) {
        f = f || {};
        var c = f.control, a = f.newIp, b = f.sameIp;
        if (!c || !c.opened) return { kind: 'unknown', why: c && c.noRelay ? 'no_relay' : 'control_failed' };
        if (a && a.opened) return { kind: 'open' };
        if (b && b.opened) return { kind: 'address' };
        if (a && b) return { kind: 'port' };
        return { kind: 'unknown', why: 'partial' };
    }
    function classifyHairpin(t, mapping) {
        if (!t || t.noSrflx) return 'unknown';
        if (t.opened) return 'yes';
        return mapping && mapping.kind === 'dependent' ? 'unknown' : 'no';
    }

    /* Console terms. Community consensus (natchecker, Rockstar support, Xbox GDK wording), not an
       official mapping: Sony publishes none. UPnP is invisible to a browser, so both branches. */
    /* extra: { cgnat, double, mismatch, vpn (measured THROUGH a VPN/relay), cellular } — facts from
       outside the probe. `upnp` is the console's type when the router opens a port for it, and it is
       null wherever UPnP cannot help: the NAT that blocks is the carrier's (cgnat), a second router's
       (double, mismatch), a VPN's, a mobile operator's, or one of several exits (multi). */
    function verdict(n, extra) {
        extra = extra || {};
        var cgnat = extra.cgnat === true, vpn = extra.vpn === true;
        var v = { ps5: {}, xbox: {}, level: 'unknown', causes: [] };
        if (!n || !n.rtc) { v.level = 'unknown'; v.causes.push('no_rtc'); return v; }
        var unk = function () { v.ps5 = { plain: 'unknown', upnp: null }; v.xbox = { plain: 'unknown', upnp: null }; };
        if (n.udp === 'blocked' || n.udp === 'firewall') {
            v.ps5 = { plain: 'fail', upnp: null }; v.xbox = { plain: 'strict', upnp: null };
            v.level = 'bad'; v.causes.push(n.udp === 'firewall' ? 'firewall' : 'udp_blocked');
            return v;
        }
        if (n.udp === 'browser') { unk(); v.causes.push('browser_blocks'); return v; }
        if (n.udp === 'offline') { unk(); v.causes.push('offline'); return v; }
        var noUpnp = cgnat || extra.double === true || extra.mismatch === true || vpn || extra.cellular === true;
        var m = n.mapping ? n.mapping.kind : 'unknown', f = n.filtering ? n.filtering.kind : 'unknown';
        if (m === 'dependent' || m === 'multi') {
            var help = !noUpnp && m !== 'multi';
            v.ps5 = { plain: 'type3', upnp: help ? 'type2' : null };
            v.xbox = { plain: 'strict', upnp: help ? 'open' : null };
            v.level = 'bad'; v.causes.push(m === 'multi' ? 'multi_exit' : 'symmetric');
        } else if (m === 'independent') {
            v.ps5 = { plain: 'type2', upnp: null };
            if (f === 'open') { v.xbox = { plain: 'open', upnp: null }; v.level = 'good'; }
            else if (f === 'address' || f === 'port') { v.xbox = { plain: 'moderate', upnp: noUpnp ? null : 'open' }; v.level = 'ok'; v.causes.push('filtering'); }
            else { v.xbox = { plain: 'unknown', upnp: null }; v.level = 'ok'; v.causes.push('filtering_unknown'); }
        } else if (m === 'interfaces') {
            unk(); v.causes.push('interfaces');
        } else {
            unk(); v.causes.push('mapping_unknown');
        }
        if (cgnat) v.causes.push('cgnat');
        if (extra.double === true) v.causes.push('double');
        if (extra.mismatch === true) v.causes.push('mismatch');
        if (vpn) v.causes.push('vpn');
        if (extra.cellular === true) v.causes.push('cellular');
        if (n.hairpin === 'no') v.causes.push('no_hairpin');
        return v;
    }

    /* A WAN address the person read off the router page. */
    // A numeric keypad in a Russian, German or French locale offers a comma, not a dot: "192,168,1,1".
    function ipv4(s) {
        var m = /^\s*(\d{1,3})[.,](\d{1,3})[.,](\d{1,3})[.,](\d{1,3})\s*$/.exec(String(s || ''));
        if (!m) return null;
        var a = [+m[1], +m[2], +m[3], +m[4]];
        if (a.some(function (x) { return x > 255; })) return null;
        return a;
    }
    function isPrivateLan(a) { return a[0] === 10 || (a[0] === 172 && a[1] >= 16 && a[1] <= 31) || (a[0] === 192 && a[1] === 168); }
    function classifyWan(wan, publicIp) {
        if (!String(wan == null ? '' : wan).trim()) return { kind: 'empty' };
        if (/:/.test(String(wan)) && /^[0-9a-f:.\s]+$/i.test(String(wan))) return { kind: 'ipv6' };
        var a = ipv4(wan);
        if (!a) return { kind: 'invalid' };
        var priv = a[0] === 10 || (a[0] === 172 && a[1] >= 16 && a[1] <= 31) || (a[0] === 192 && a[1] === 168);
        var cg = a[0] === 100 && a[1] >= 64 && a[1] <= 127;
        if (cg) return { kind: 'cgnat' };
        if (priv) return { kind: 'double' };
        if (a[0] === 0 || a[0] === 127 || a[0] >= 224 || (a[0] === 169 && a[1] === 254)) return { kind: 'invalid' };
        if (publicIp && a.join('.') === publicIp) return { kind: 'public', ip: a.join('.') };
        if (publicIp) return { kind: 'mismatch' };
        return { kind: 'public_unverified' };
    }

    /* Ports from the OFFICIAL lists only (sources in docs/why/nat-type-test.md). GTA Online's are
       Rockstar's for GTA V — GTA VI ports are not announced. */
    var PORTS = {
        ps5: { tcp: ['80', '443', '3478', '3479', '3480'], udp: ['3478', '3479', '49152-65535'] },
        xbox: { tcp: ['53', '80', '3074'], udp: ['53', '88', '500', '3074', '3544', '4500'] },
        gtao: { tcp: [], udp: ['6672', '61455-61458'] }
    };
    /* The console's LAN address for port rules: only a private address can be a console behind the
       router; ".1" is almost always the router itself. */
    function lanAddress(s) {
        if (!String(s == null ? '' : s).trim()) return { kind: 'empty', ip: '' };
        var a = ipv4(s);
        if (!a) return { kind: 'invalid', ip: '' };
        if (!isPrivateLan(a)) return { kind: 'not_lan', ip: '' };
        var ip = a.join('.');
        return { kind: a[3] === 1 || a[3] === 254 ? 'router' : 'ok', ip: ip };
    }
    function portRules(consoleKind, localIp, withGta) {
        var p = PORTS[consoleKind]; if (!p) return [];
        var la = lanAddress(localIp);
        var ip = la.kind === 'ok' || la.kind === 'router' ? la.ip : '';
        var rows = [];
        p.tcp.forEach(function (x) { rows.push({ proto: 'TCP', ports: x, to: ip }); });
        p.udp.forEach(function (x) { rows.push({ proto: 'UDP', ports: x, to: ip }); });
        if (withGta) PORTS.gtao.udp.forEach(function (x) { rows.push({ proto: 'UDP', ports: x, to: ip, gta: true }); });
        return rows;
    }

    /* ---------- line quality ---------- */
    function mungeOpus(sdp) {
        // receiver's wishes for the sender: no DTX (silence would thin the stream), no in-band FEC,
        // 20 ms packets — a steady 50 packets/s, the cadence of a game's tick
        var pt = (/a=rtpmap:(\d+) opus\/48000/i.exec(sdp) || [])[1];
        if (!pt) return sdp;
        var re = new RegExp('a=fmtp:' + pt + ' ([^\\r\\n]*)');
        if (re.test(sdp)) {
            sdp = sdp.replace(re, function (_, p) {
                var kv = p.split(';').filter(function (x) { return !/^(usedtx|useinbandfec|maxaveragebitrate)=/.test(x.trim()); });
                kv.push('usedtx=0', 'useinbandfec=0', 'maxaveragebitrate=24000');
                return 'a=fmtp:' + pt + ' ' + kv.join(';');
            });
        }
        if (!/a=ptime:/.test(sdp)) sdp = sdp.replace(/(m=audio[^\r\n]*\r\n)/, '$1a=ptime:20\r\n');
        return sdp;
    }

    function percentile(sorted, p) { if (!sorted.length) return null; return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))]; }

    /* One loop through a region's TURN: X sends Opus + a 10 Hz timestamped ping, Y receives. */
    async function quality(opts) {
        var region = opts.region, seconds = Math.max(5, Math.min(600, opts.seconds || 30));
        var signal = opts.signal, ctx = opts.audioCtx, onTick = opts.onTick || function () {};
        var res = { region: region, ok: false };
        var turn = opts.turn || turnOf(region, 'udp');   // opts.turn: stands only (a lossy proxy in front of ours)
        if (!turn || !hasRtc() || !ctx) { res.why = !ctx ? 'no_audio' : 'no_rtc'; return res; }
        var X = null, Y = null, nodes = [], tick = null, ping = null;
        function cleanup() {
            clearInterval(tick); clearInterval(ping);
            nodes.forEach(function (n) { try { n.stop && n.stop(); n.disconnect && n.disconnect(); } catch (_) {} });
            closeAll([X, Y]);
        }
        try {
            var osc = ctx.createOscillator(); osc.frequency.value = 523;
            var g = ctx.createGain(); g.gain.value = 0.3;
            var buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate), d = buf.getChannelData(0);
            for (var i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.15;
            var noise = ctx.createBufferSource(); noise.buffer = buf; noise.loop = true;
            var dest = ctx.createMediaStreamDestination();
            osc.connect(g); noise.connect(g); g.connect(dest); osc.start(); noise.start();
            nodes.push(osc, noise, g);
            X = new RTCPeerConnection({ iceServers: [turn], iceTransportPolicy: 'relay' });
            Y = new RTCPeerConnection({ iceServers: [turn], iceTransportPolicy: 'relay' });
            X.onicecandidate = function (e) { if (e.candidate) Y.addIceCandidate(e.candidate).catch(function () {}); };
            Y.onicecandidate = function (e) { if (e.candidate) X.addIceCandidate(e.candidate).catch(function () {}); };
            var track = dest.stream.getAudioTracks()[0];
            X.addTrack(track, dest.stream);
            var pch = X.createDataChannel('ping', { ordered: false, maxRetransmits: 0 });
            var lat = [], latSeries = [];
            Y.ondatachannel = function (e) {
                e.channel.binaryType = 'arraybuffer';
                e.channel.onmessage = function (ev) {
                    try { var v = new DataView(ev.data); var ms = now() - v.getFloat64(4); lat.push(ms); latSeries.push([Math.round(now()), ms]); } catch (_) {}
                };
            };
            var connected = false;
            Y.onconnectionstatechange = function () { if (Y.connectionState === 'connected') connected = true; };
            Y.ontrack = function () {};
            await X.setLocalDescription(await X.createOffer());
            await Y.setRemoteDescription(X.localDescription);
            var ans = await Y.createAnswer();
            await Y.setLocalDescription(ans);
            await X.setRemoteDescription({ type: 'answer', sdp: mungeOpus(Y.localDescription.sdp) });
            var t0 = now();
            while (!connected && now() - t0 < 15000 && !aborted(signal)) await sleep(50);
            if (!connected) { res.why = aborted(signal) ? 'aborted' : 'no_connect'; cleanup(); return res; }
            res.setupMs = Math.round(now() - t0);
            var read = async function () { var r = null, st = await statsOf(Y, 1500); if (st) st.forEach(function (s) { if (s.type === 'inbound-rtp' && s.kind === 'audio') r = s; }); return r; };
            var base = null; t0 = now();
            while (now() - t0 < 8000 && !aborted(signal)) { base = await read(); if (base && base.packetsReceived > 25) break; await sleep(100); }
            if (!base) { res.why = 'no_rtp'; cleanup(); return res; }
            var seq = 0, pbuf = new ArrayBuffer(64), pv = new DataView(pbuf);
            ping = setInterval(function () {
                if (pch.readyState !== 'open') return;
                pv.setUint32(0, seq++); pv.setFloat64(4, now());
                try { pch.send(pbuf); } catch (_) {}
            }, 100);
            var series = [], last = base, start = now(), maxLostWin = 0, lossWins = 0;
            // "Under load": the first part of the run is the quiet baseline, the rest runs beside a
            // download, so one loop gives both numbers on the same path a few seconds apart.
            var load = null, loadAfter = opts.load ? Math.max(0, opts.loadAfterMs || 0) : -1;
            var capBytes = opts.loadCapBytes || LOAD.capBytes, loadWin = opts.loadWindowMs || LOAD.windowMs;
            if (loadAfter === 0) { load = startLoad(signal, capBytes); res.loadAtMs = 0; }
            await new Promise(function (resolve) {
                tick = setInterval(async function () {
                    var s = null; try { s = await read(); } catch (_) {}
                    if (!s) return;
                    var dl = Math.max(0, (s.packetsLost || 0) - (last.packetsLost || 0));
                    var dr = Math.max(0, (s.packetsReceived || 0) - (last.packetsReceived || 0));
                    last = s;
                    if (dl > 0) { lossWins++; if (dl > maxLostWin) maxLostWin = dl; }
                    var tNow = now(), recent = lat.slice(-3);
                    if (!load && loadAfter > 0 && tNow - start >= loadAfter) { load = startLoad(signal, capBytes); res.loadAtMs = Math.round(tNow - start); }
                    if (load && !load.done() && res.loadAtMs != null && tNow - start >= res.loadAtMs + loadWin) load.stop();
                    if (load && load.done() && res.loadEndMs == null) res.loadEndMs = Math.round(tNow - start);
                    var point = { t: Math.round(tNow - start), lost: dl, recv: dr, lat: recent.length ? recent[recent.length - 1] : null, load: load ? 1 : 0 };
                    series.push(point);
                    onTick({ region: region, elapsed: (tNow - start) / 1000, seconds: seconds, point: point,
                             lost: (s.packetsLost || 0) - (base.packetsLost || 0), recv: (s.packetsReceived || 0) - (base.packetsReceived || 0) });
                    if (tNow - start >= seconds * 1000 || aborted(signal)) resolve();
                }, 250);
            });
            if (load) { load.stop(); if (res.loadEndMs == null) res.loadEndMs = Math.round(now() - start); }
            res.stopped = aborted(signal);
            var fin = await read() || last;
            var rtcp = null;
            try { var xs = await statsOf(X, 1500); if (xs) xs.forEach(function (s) { if (s.type === 'remote-inbound-rtp' && s.roundTripTime != null) rtcp = s.roundTripTime; }); } catch (_) {}
            var recv = (fin.packetsReceived || 0) - (base.packetsReceived || 0);
            var lost = Math.max(0, (fin.packetsLost || 0) - (base.packetsLost || 0));
            var total = recv + lost;
            var sl = lat.slice().sort(function (a, b) { return a - b; });
            var p50 = percentile(sl, 0.5);
            var spikes = 0; lat.forEach(function (x) { if (p50 != null && x > p50 + Math.max(50, p50)) spikes++; });
            res.ok = true;
            res.seconds = Math.round((now() - start) / 1000);
            res.recv = recv; res.lost = lost; res.total = total;
            res.lossPct = total ? 100 * lost / total : 0;
            res.lossUpperPct = lost === 0 && total ? 100 * 3 / total : null;     // rule of three: zero seen ≠ zero
            res.lossWindows = lossWins; res.maxBurst = maxLostWin;
            res.jitterMs = fin.jitter != null ? fin.jitter * 1000 : null;
            res.latency = { p50: p50, p95: percentile(sl, 0.95), max: sl.length ? sl[sl.length - 1] : null, n: sl.length };
            res.spikes = spikes;
            res.rtcpRttMs = rtcp != null ? rtcp * 1000 : null;
            res.pingSent = seq; res.pingRecv = lat.length;
            res.series = series;
            if (load) {
                // quiet = before the download; loaded = while it ran (its first 2 s are the ramp)
                var q0 = [], q1 = [], la = res.loadAtMs || 0, le = res.loadEndMs || Infinity;
                latSeries.forEach(function (p) { var t = p[0] - start; if (t < la) q0.push(p[1]); else if (t > la + 2000 && t <= le) q1.push(p[1]); });
                res.loadedSamples = q1.length; res.loadCapHit = load.capped();
                q0.sort(function (a, b) { return a - b; }); q1.sort(function (a, b) { return a - b; });
                res.quietP50 = percentile(q0, 0.5); res.loadedP50 = percentile(q1, 0.5); res.loadedP95 = percentile(q1, 0.95);
            }
            res.loaded = !!opts.load; res.loadBytes = load ? load.bytes() : 0;
            cleanup();
            return res;
        } catch (e) {
            res.why = 'error'; res.error = String(e && e.message || e);
            cleanup(); return res;
        }
    }

    /* Background download for "what happens when someone at home streams" — bounded by the caller's
       duration; bytes are reported so the page can say how much it took. */
    /* Before 04.11 this ran for the rest of the run with no cap — a 30 s run took 282 MB while the
       label said "up to 150 MB" (QA M3). Now: at most capBytes, and the caller stops it after its
       window; a link fast enough to hit the cap early simply ends the loaded window early. */
    function startLoad(signal, capBytes) {
        var stopped = false, capped = false, bytes = 0, ctrls = [];
        capBytes = capBytes || LOAD.capBytes;
        function one() {
            if (stopped) return;
            var c = new AbortController(); ctrls.push(c);
            fetch('/speedtest/d25m.bin?nat=' + Math.random().toString(36).slice(2), { cache: 'no-store', signal: c.signal })
                .then(function (r) {
                    if (!r.ok || !r.body) throw new Error('load');
                    var rd = r.body.getReader();
                    var pump = function () {
                        return rd.read().then(function (x) {
                            if (x.done || stopped) return;
                            bytes += x.value.byteLength;
                            if (bytes >= capBytes) { capped = true; stop(); return; }
                            return pump();
                        });
                    };
                    return pump();
                }).catch(function () {}).then(function () { if (!stopped) setTimeout(one, 50); });
        }
        for (var i = 0; i < 3; i++) one();
        onAbort(signal, function () { stop(); });
        function stop() { stopped = true; ctrls.forEach(function (c) { try { c.abort(); } catch (_) {} }); }
        return { stop: stop, bytes: function () { return bytes; }, done: function () { return stopped; }, capped: function () { return capped; } };
    }

    /* Plain-language grade of a loop result for online play. Loss/jitter thresholds follow the
       usual guidance for real-time games (≲0.5 % unnoticed, >2 % felt as rubber-banding). */
    var LIMITS = { loss: [0.5, 2], jitter: [15, 30], spikes: [0.01, 0.05] };
    function issues(q) {
        if (!q || !q.ok) return null;
        var n = (q.latency && q.latency.n) || 1, sr = (q.spikes || 0) / n, j = q.jitterMs || 0;
        var lvl = function (v, lim) { return v > lim[1] ? 2 : (v > lim[0] ? 1 : 0); };
        return { loss: lvl(q.lossPct, LIMITS.loss), jitter: lvl(j, LIMITS.jitter), spikes: lvl(sr, LIMITS.spikes) };
    }
    function grade(q) {
        var i = issues(q); if (!i) return 'unknown';
        var worst = Math.max(i.loss, i.jitter, i.spikes);
        return worst === 2 ? 'bad' : (worst === 1 ? 'ok' : 'good');
    }
    /* The line is as good as its WORST measured path: players in Russia play on EU servers, so loss
       to Amsterdam is their loss even when St Petersburg is clean (QA M10). */
    var GRADE_RANK = { unknown: -1, good: 0, ok: 1, bad: 2 };
    function worstGrade(list) {
        var best = { grade: 'unknown', region: null };
        (list || []).forEach(function (q) {
            var g = grade(q);
            if (GRADE_RANK[g] > GRADE_RANK[best.grade]) best = { grade: g, region: q.region };
        });
        return best;
    }

    /* What this site sees vs what STUN saw: a VPN, a proxy or iCloud Private Relay in the path. */
    // Bounded: a stalled request used to hold the finished page on "0 s left" (QA M4).
    async function serverView(publicIp, signal, ms) {
        var c = new AbortController();
        onAbort(signal, function () { c.abort(); });
        var timedOut = false, t = setTimeout(function () { timedOut = true; c.abort(); }, ms || 5000);
        try {
            var r = await fetch('/api/connection-check' + (publicIp ? '?ips=' + encodeURIComponent(publicIp) : ''), { cache: 'no-store', signal: c.signal });
            if (!r.ok) return { failed: 'http_' + r.status };
            var j = await r.json();
            var you = j && j.you || {};
            var udpSide = (j.others || [])[0] || (publicIp === you.ip ? you : null);
            return {
                httpIp: you.ip || '', httpClass: you['class'] || 'unknown', org: you.org || '', country: you.countryCode || '',
                udpOrg: udpSide ? udpSide.org : '', udpClass: udpSide ? udpSide['class'] : '',
                // only two PUBLIC addresses that disagree mean a different way out; a private one is
                // a local or proxied setup that says nothing about the path (dev stand: 127.0.0.1)
                differs: !!(publicIp && you.ip && you.ip !== publicIp && you.version === 4 && you['class'] !== 'private')
            };
        } catch (_) { return { failed: timedOut ? 'timeout' : 'error' }; }
        finally { clearTimeout(t); }
    }

    w.NatProbe = {
        nat: nat, quality: quality, serverView: serverView,
        classifyMapping: classifyMapping, classifyFiltering: classifyFiltering, classifyHairpin: classifyHairpin,
        verdict: verdict, classifyWan: classifyWan, lanAddress: lanAddress, portRules: portRules, grade: grade, worstGrade: worstGrade,
        issues: issues, LIMITS: LIMITS, LOAD: LOAD, mungeOpus: mungeOpus, ipv4: ipv4,
        PORTS: PORTS, regions: regions, _stunProbe: stunProbe, _mappingProbe: mappingProbe, _pairTest: pairTest
    };
})(window);
